import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Grid, Typography, Link, IconButton, Button, styled } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const FooterSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: '#fff',
  padding: '4rem 0 2rem',
}));

const FooterLogo = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: '#fff',
  fontWeight: 700,
  fontSize: '1.5rem',
  marginBottom: theme.spacing(2),
  '& span': {
    color: theme.palette.primary.main,
  },
}));

// Directly style the RouterLink component
const FooterLink = styled(RouterLink)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.8)',
  transition: 'color 0.3s ease',
  textDecoration: 'none',
  display: 'block',
  marginBottom: theme.spacing(1),
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

const FooterHeading = styled(Typography)(({ theme }) => ({
  color: '#fff',
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '40px',
    height: '3px',
    backgroundColor: theme.palette.primary.main,
  },
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  '& > svg': {
    marginRight: theme.spacing(1),
    marginTop: '3px',
    color: theme.palette.primary.main,
  },
}));

const CopyrightSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#000',
  color: 'rgba(255, 255, 255, 0.7)',
  padding: theme.spacing(2, 0),
  textAlign: 'center',
}));

const Footer: React.FC = () => {
  const [phone1, setPhone1] = useState<string>('');
  const [phone2, setPhone2] = useState<string>('');

  useEffect(() => {
    // Set phone numbers after component mounts
    setPhone1('9202558952');
    setPhone2('9202654729');
  }, []);

  return (
    <Box component="footer">
      <FooterSection>
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <FooterLogo>
                JBR <span>Delivery</span> LLC
              </FooterLogo>
              <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mb: 2 }}>
                Professional delivery, junk removal, and moving services in Northeast Wisconsin. Fast, reliable, and affordable solutions.
              </Typography>
              <Box display="flex">
                <IconButton 
                  aria-label="Facebook" 
                  component="a" 
                  href="https://www.facebook.com/p/JBR-Delivery-Service-LLC-100076548537991/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: '#fff', 
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    mr: 1,
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    }
                  }}
                >
                  <FacebookIcon />
                </IconButton>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FooterHeading variant="h6">Quick Links</FooterHeading>
              <FooterLink to="/">Home</FooterLink>
              <FooterLink to="/about">About Us</FooterLink>
              <FooterLink to="/services/delivery">Delivery Services</FooterLink>
              <FooterLink to="/services/junk-removal">Junk Removal</FooterLink>
              <FooterLink to="/services/moving">Moving Services</FooterLink>
              <FooterLink to="/services/specialty">Specialty Services</FooterLink>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FooterHeading variant="h6">Customer Resources</FooterHeading>
              <FooterLink to="/areas">Service Areas</FooterLink>
              <FooterLink to="/pricing">Pricing Information</FooterLink>
              <FooterLink to="/faq">FAQ</FooterLink>
              <FooterLink to="/blog">Project Blog</FooterLink>
              <FooterLink to="/contact">Contact Us</FooterLink>
              <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
              <FooterLink to="/terms-of-service">Terms of Service</FooterLink>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FooterHeading variant="h6">Contact Info</FooterHeading>
              <ContactItem>
                <PhoneIcon />
                <Box>
                  {phone1 && (
                    <Link href={`tel:${phone1}`} color="inherit" sx={{ display: 'block' }}>
                      {`(${phone1.substring(0, 3)}) ${phone1.substring(3, 6)}-${phone1.substring(6)}`}
                    </Link>
                  )}
                  {phone2 && (
                    <Link href={`tel:${phone2}`} color="inherit">
                      {`(${phone2.substring(0, 3)}) ${phone2.substring(3, 6)}-${phone2.substring(6)}`}
                    </Link>
                  )}
                </Box>
              </ContactItem>
              
              <ContactItem>
                <EmailIcon />
                <Link 
                  href="mailto:jbrdeliveryllc@gmail.com" 
                  color="inherit"
                >
                  jbrdeliveryllc@gmail.com
                </Link>
              </ContactItem>
              
              <ContactItem>
                <LocationOnIcon />
                <Typography variant="body2">
                  Serving the Greater Green Bay Area, Door County, and Northeast Wisconsin
                </Typography>
              </ContactItem>
              
              <Button
                variant="contained"
                color="primary"
                startIcon={<PhoneIcon />}
                component={RouterLink}
                to="/contact"
                fullWidth
                sx={{ mt: 2 }}
              >
                Request a Quote
              </Button>
            </Grid>
          </Grid>
        </Container>
      </FooterSection>
      
      <CopyrightSection>
        <Container>
          <Typography variant="body2">
            &copy; {new Date().getFullYear()} JBR Delivery LLC - All Rights Reserved
          </Typography>
        </Container>
      </CopyrightSection>
    </Box>
  );
};

export default Footer;
